import { useState, useEffect } from 'react';
import { Outlet, NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import logo from '../images/logo.png';
import { useAuth } from '../hooks/useAuth';
import ColorModeToggle from './ColorModeToggle';
import { PURPLE_AVATAR, PURPLE_FOOTER } from '../theme/tokens';

const NAV_LINK_SX = {
    color: 'rgba(255,255,255,0.9)',
    textDecoration: 'none',
    px: 1.5,
    py: 0.5,
    borderRadius: 1,
    fontSize: '0.875rem',
    fontWeight: 500,
    '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
    '&.active': { color: '#fff', bgcolor: 'rgba(255,255,255,0.18)' },
};

const FOOTER_LINK_SX = { color: 'rgba(255,255,255,0.5)', textDecoration: 'none', '&:hover': { color: '#fff' } };

export default function PublicLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [learnAnchor, setLearnAnchor] = useState<null | HTMLElement>(null);
    const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => { setLearnAnchor(null); }, [location.pathname]);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 0);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleLogout = async () => {
        setUserMenuAnchor(null);
        await logout();
        navigate('/login');
    };

    const navLinks = (
        <>
            {user && <li><Box component={NavLink} to="/badges" sx={NAV_LINK_SX}>Manage Badges</Box></li>}
            {user && <li><Box component={NavLink} to="/available-badges" sx={NAV_LINK_SX}>Discover</Box></li>}
            {user && <li><Box component={NavLink} to="/issuers" sx={NAV_LINK_SX}>My Issuers</Box></li>}
            <li><Box component={NavLink} to="/about" sx={NAV_LINK_SX}>About</Box></li>
            <li><Box component={NavLink} to="/how-it-works" sx={NAV_LINK_SX}>How It Works</Box></li>
            <li>
                <Button
                    variant="text"
                    onClick={(e) => setLearnAnchor(e.currentTarget)}
                    endIcon={<KeyboardArrowDownIcon />}
                    sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}
                >
                    Learn More
                </Button>
            </li>
            <li><Box component={NavLink} to="/faqs" sx={NAV_LINK_SX}>FAQs</Box></li>
            <li><Box component={NavLink} to="/contact" sx={NAV_LINK_SX}>Contact</Box></li>
        </>
    );

    const authButtons = (
        <Box display="flex" gap={1} alignItems="center" sx={{ position: 'absolute', right: 16 }}>
            <ColorModeToggle />
            {user ? (
                <>
                    <IconButton onClick={(e) => setUserMenuAnchor(e.currentTarget)} size="small">
                        <Avatar
                            src={user.profileImage ?? undefined}
                            alt={user.displayName}
                            sx={{ width: 36, height: 36, bgcolor: PURPLE_AVATAR }}
                        >
                            {!user.profileImage && (user.firstName?.[0] ?? '?')}
                        </Avatar>
                    </IconButton>
                    <Menu
                        anchorEl={userMenuAnchor}
                        open={Boolean(userMenuAnchor)}
                        onClose={() => setUserMenuAnchor(null)}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                        <MenuItem component={NavLink} to="/profile" onClick={() => setUserMenuAnchor(null)}>
                            <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
                            My Profile
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={handleLogout}>
                            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                            Logout
                        </MenuItem>
                    </Menu>
                </>
            ) : (
                <>
                    <Button component={NavLink} to="/login" variant="text"
                        sx={{ color: '#fff' }}>
                        Sign In
                    </Button>
                    <Button component={NavLink} to="/register" variant="contained" color="secondary" size="small" sx={{ fontWeight: 700 }}>
                        Get Started
                    </Button>
                </>
            )}
        </Box>
    );

    return (
        <Box display="flex" flexDirection="column" minHeight="100vh">
            {/* Navbar */}
            <AppBar
                component="nav"
                position="sticky"
            >
                <Toolbar
                    sx={{
                        justifyContent: scrolled ? 'flex-start' : 'center',
                        minHeight: scrolled ? 48 : 160,
                        position: 'relative',
                        transition: 'min-height 0.25s ease',
                    }}
                >
                    <Link
                        to="/"
                        style={{
                            display: 'flex',
                            marginRight: scrolled ? 24 : 0,
                        }}
                    >
                        <Box
                            component="img"
                            src={logo}
                            alt="TC Everywhere"
                            sx={{
                                maxHeight: scrolled ? 32 : 140,
                                maxWidth: '90vw',
                                width: 'auto',
                                transition: 'max-height 0.25s ease',
                            }}
                        />
                    </Link>

                    {/* Inline nav when scrolled */}
                    {scrolled && (
                        <Box component="ul" display="flex" alignItems="center" gap={0} sx={{ listStyle: 'none', m: 0, p: 0, flexGrow: 1 }}>
                            {navLinks}
                        </Box>
                    )}

                    {authButtons}
                </Toolbar>

                {/* Full nav bar when not scrolled */}
                {!scrolled && (
                    <Toolbar variant="dense" sx={{ justifyContent: 'center', gap: 0.5, minHeight: 40, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                        <Box component="ul" display="flex" alignItems="center" gap={0} sx={{ listStyle: 'none', m: 0, p: 0 }}>
                            {navLinks}
                        </Box>
                    </Toolbar>
                )}

                {/* Shared Learn More menu */}
                <Menu anchorEl={learnAnchor} open={Boolean(learnAnchor)} onClose={() => setLearnAnchor(null)}>
                    <MenuItem component={NavLink} to="/audience" onClick={() => setLearnAnchor(null)}>Who Is It For?</MenuItem>
                    <MenuItem component={NavLink} to="/why-free" onClick={() => setLearnAnchor(null)}>Why Is This Free?</MenuItem>
                    <MenuItem component={NavLink} to="/roadmap" onClick={() => setLearnAnchor(null)}>Road Map</MenuItem>
                    <MenuItem component={NavLink} to="/partners" onClick={() => setLearnAnchor(null)}>Partners</MenuItem>
                    <MenuItem component={NavLink} to="/developers" onClick={() => setLearnAnchor(null)}>Developers &amp; Integrations</MenuItem>
                </Menu>
            </AppBar>

            {/* Page content */}
            <Box component="main" flexGrow={1}>
                <Outlet />
            </Box>

            {/* Footer */}
            <Box component="footer" sx={{ bgcolor: PURPLE_FOOTER, color: 'rgba(255,255,255,0.7)', pt: 6, pb: 3, mt: 6 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4} mb={4}>
                        <Grid item xs={12} md={4}>
                            <Box mb={1.5}>
                                <Box component="img" src={logo} alt="TC Everywhere" sx={{ height: 40 }} />
                            </Box>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                Open Badges for everyone. Issue, earn, and share verifiable digital credentials that showcase real achievement.
                            </Typography>
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <Typography variant="subtitle2" fontWeight={600} mb={1.5}>Product</Typography>
                            {[
                                { to: '/how-it-works', label: 'How It Works' },
                                { to: '/audience', label: 'Who Is It For?' },
                                { to: '/why-free', label: 'Why Is This Free?' },
                                { to: '/roadmap', label: 'Road Map' },
                            ].map(l => (
                                <Box key={l.to} mb={0.5}><Box component={Link} to={l.to} sx={{ ...FOOTER_LINK_SX, fontSize: '0.875rem' }}>{l.label}</Box></Box>
                            ))}
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <Typography variant="subtitle2" fontWeight={600} mb={1.5}>Company</Typography>
                            {[
                                { to: '/about', label: 'About Us' },
                                { to: '/partners', label: 'Partners' },
                                { to: '/contact', label: 'Contact Us' },
                            ].map(l => (
                                <Box key={l.to} mb={0.5}><Box component={Link} to={l.to} sx={{ ...FOOTER_LINK_SX, fontSize: '0.875rem' }}>{l.label}</Box></Box>
                            ))}
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <Typography variant="subtitle2" fontWeight={600} mb={1.5}>Developers</Typography>
                            {[
                                { to: '/developers', label: 'Integrations' },
                                { to: '/faqs', label: 'FAQs' },
                                { to: '/privacy', label: 'Privacy Policy' },
                            ].map(l => (
                                <Box key={l.to} mb={0.5}><Box component={Link} to={l.to} sx={{ ...FOOTER_LINK_SX, fontSize: '0.875rem' }}>{l.label}</Box></Box>
                            ))}
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <Typography variant="subtitle2" fontWeight={600} mb={1.5}>Account</Typography>
                            {user ? (
                                <>
                                    <Box mb={0.5}><Box component={Link} to="/" sx={{ ...FOOTER_LINK_SX, fontSize: '0.875rem' }}>Dashboard</Box></Box>
                                    <Box mb={0.5}><Box component={Link} to="/badges" sx={{ ...FOOTER_LINK_SX, fontSize: '0.875rem' }}>Manage Badges</Box></Box>
                                    <Box mb={0.5}>
                                        <Box component="button" onClick={handleLogout}
                                            sx={{ ...FOOTER_LINK_SX, fontSize: '0.875rem', background: 'none', border: 'none', cursor: 'pointer', p: 0 }}>
                                            Logout
                                        </Box>
                                    </Box>
                                </>
                            ) : (
                                <>
                                    <Box mb={0.5}><Box component={Link} to="/login" sx={{ ...FOOTER_LINK_SX, fontSize: '0.875rem' }}>Sign In</Box></Box>
                                    <Box mb={0.5}><Box component={Link} to="/register" sx={{ ...FOOTER_LINK_SX, fontSize: '0.875rem' }}>Get Started Free</Box></Box>
                                </>
                            )}
                        </Grid>
                    </Grid>
                    <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', pt: 2, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                            © {new Date().getFullYear()} TC Everywhere. All rights reserved.
                        </Typography>
                        <Box display="flex" gap={3}>
                            <Box component={Link} to="/privacy" sx={{ ...FOOTER_LINK_SX, fontSize: '0.8rem' }}>Privacy Policy</Box>
                            <Box component={Link} to="/contact" sx={{ ...FOOTER_LINK_SX, fontSize: '0.8rem' }}>Contact</Box>
                        </Box>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}
